import * as React from "react";
import {CmsContent} from "../../../Model/CmsContent/CmsContent";
import {optHtmlContent, renderDynamicContent} from "../../../Utilities/CmsUtilities";
import PromoCodeComponent from "../../../Components/Superdrug/Card/PromoCodeComponent";
import {SimOnlyPlan} from "../../../Model/Sales/SimOnlyPlan";
import {PlanPrice} from "../../../Model/Sales/PlanPrice";
import {UserDetails} from "../../../Model/User/UserDetails";
import {RafData} from "../../RafPage/RafPage";
import PortInInput from "./PortInInput";
import {currencyMinimumDecimals} from "../../../Model/Configuration/CurrencyConfiguration";
import {ApplicationContext} from "../../../ApplicationContext";

interface PlanPurchasePaymentSummaryProps {
    readonly componentData: {
        readonly selectedPlan: SimOnlyPlan;
        readonly selectedPlanPriceWithPromo: PlanPrice | null;
        readonly userDetails?: UserDetails;
    },
    setPromoCode?: (promo: string | null) => void,
    setSelectedPlanPriceWithPromo: ((planPrice: PlanPrice | null) => void | null),
    setSelectedPlan: (simOnlyPlan: SimOnlyPlan) => void

    readonly promoCode?: string | null
    readonly maybeRafData: RafData | null
    readonly chosenPlanInfo: SimOnlyPlan
    readonly deliveryPrice: number
    readonly paidInStorePlanPrice: number
    readonly priceToPay: number
    readonly isSimInHandJourney: boolean | undefined
    readonly hasLeadGenerator: boolean | undefined
    readonly formId: string

    readonly cmsContent: CmsContent[];
}

/**
 * Function will render the payment summary including price breakdown, referrals, and promo codes
 */
const PlanPurchasePaymentSummary = (props: PlanPurchasePaymentSummaryProps): JSX.Element | null => {
    const applicationContext = React.useContext(ApplicationContext)
    const optPlanPurchasePaymentSummary = optHtmlContent(props.cmsContent, "sp-purchase-payment-info-component")

    const optReferralCard = optHtmlContent(props.cmsContent, "sp-card-referral-info")
    const optDeliverySummaryCard = optHtmlContent(props.cmsContent, "sp-card-delivery-summary")
    const optPaidInStoreDiscountSummary = optHtmlContent(props.cmsContent, "sp-card-paid-in-store-discount-summary")
    const optCreditComponent = optHtmlContent(props.cmsContent, "sp-credit-component")



    if (props.chosenPlanInfo && optDeliverySummaryCard && optPaidInStoreDiscountSummary
        && optCreditComponent && props.paidInStorePlanPrice != undefined && optPlanPurchasePaymentSummary) {

        return (
            <>
                {renderDynamicContent(optPlanPurchasePaymentSummary, [
                    {
                        key: "PROMO_CODE_COMPONENT",
                        value: <PromoCodeComponent componentData={props.componentData}
                                                   setSelectedPlanPriceWithPromo={props.setSelectedPlanPriceWithPromo}
                                                   setSelectedPlan={props.setSelectedPlan}
                                                   setPromoCode={props.setPromoCode}
                                                   useQueryParamPromo={!props.isSimInHandJourney}
                        promoCode={props.promoCode}/>
                    },
                    {key: "REFERRAL_COMPONENT", value: props.maybeRafData ? optReferralCard : null},
                    {key: "PLAN_DESCRIPTION", value: props.chosenPlanInfo.description},
                    {
                        key: "PLAN_PRICE",
                        value: `${currencyMinimumDecimals(props.componentData.selectedPlanPriceWithPromo?.priceToPay
                            ?? props.chosenPlanInfo.monthlyPrice, applicationContext.appConfig.currencyConfiguration)}`
                    },
                    {
                        key: "DELIVERY_SUMMARY",
                        value: props.isSimInHandJourney ? null : renderDynamicContent(optDeliverySummaryCard, [{
                            key: "SIM_DELIVERY_PRICE",
                            value: `${currencyMinimumDecimals(props.deliveryPrice, applicationContext.appConfig.currencyConfiguration)}`
                        }])
                    },
                    {
                        key: "PAID_IN_STORE_DISCOUNT_SUMMARY",
                        value: props.isSimInHandJourney && !props.hasLeadGenerator ? renderDynamicContent(optPaidInStoreDiscountSummary, [{
                            key: "PAID_IN_STORE_DISCOUNT",
                            value: `-${currencyMinimumDecimals(props.paidInStorePlanPrice, applicationContext.appConfig.currencyConfiguration)}`
                        }]) : null
                    },
                    {key: "TOTAL_PRICE", value: props.priceToPay < 0 ? "£0" : `${currencyMinimumDecimals(props.priceToPay, applicationContext.appConfig.currencyConfiguration)}`},
                    {key: "CREDIT_COMPONENT", value: props.priceToPay < 0 ? renderDynamicContent(optCreditComponent, [
                            {key: "CREDIT_AMOUNT", value: `${-props.priceToPay}`}
                        ]) : null},
                    {key: "PORT_IN_COMPONENT", value: props.isSimInHandJourney ? <PortInInput
                            formId={props.formId}
                            cmsContent={props.cmsContent}
                        /> : null},
                    {
                        key: "PLAN_PREVIEW_CHECKBOX", value: <>
                            <div className="form-row">
                                <div className="sp-form-field-wrapper-checkbox">
                                    <input className="checkbox" type="checkbox" id="previewCheckbox" name="previewCheckbox"/>
                                </div>
                                <div className="sp-form-field-tooltip-wrapper">
                                    <label htmlFor="previewCheckbox">
                                        Tick the box to let us know you’ve read and agree to our {" "}
                                        <a href={`/register/sim-only/deals/${props.componentData.selectedPlan.packageCode}/plan-agreement-preview`}
                                           target="_blank" rel="noreferrer">
                                            Plan Information PDF, Plan Summary PDF
                                        </a>
                                        {" "} and <a href="https://help.superdrugmobile.com/en/articles/12381543-superdrug-mobile-terms-conditions" target="_blank"
                                                     rel="noreferrer">
                                        Superdrug Mobile Terms & Conditions
                                    </a>.
                                        <br></br>
                                        {props.isSimInHandJourney
                                            ? "We’ll connect your SIM as soon as you’ve done this."
                                            : "We’ll place your order as soon as you’ve done this."}
                                    </label>
                                </div>
                            </div>
                            <div
                                id={`${props.formId}:previewCheckbox-error-wrapper`}
                                className="sp-form-field-error-wrapper"
                                style={{display: "none"}}>

                                <div id={`${props.formId}:previewCheckbox-error`}
                                     className="sp-form-text-error"></div>
                            </div>
                        </>

                    }
                ])}

            </>
        )
    } else {
        return null // Prevent page flicker while we are getting everything for the page.
    }
};


export default PlanPurchasePaymentSummary